// CUSTOMER-PROMISED DATES -- M3.1 (owner decision B3, D191).
//
// The date WE promised the customer, per line of a Won (Accepted) quote.
//
// ⚠️ OPTIONAL AND NEVER DEFAULTED. An empty date travels as absent/null and
// reads "Customer date not stated" -- never today, never a guess.
//
// ⚠️ THE SERVER DECIDES. Who may set a date (Sales own / Admin), on which
// quotes (Won only), and both flags ("OEM promise after customer date",
// "Customer date at risk") are the server's, derived on read. This file shows
// them and sends what was typed.
//
// ⚠️ NOTHING HERE PRICES, and nothing here reaches a customer document.

import { useCallback, useEffect, useState } from "react";
import {
  getOrderStatus, setCustomerDates,
  type CustomerDateEntry, type OrderStatusLine,
} from "@/services/api";
import { formatDate } from "@/lib/format";
import { Pill } from "@/components/OemConfirmationsPanel";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary,
};
const BTN: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[10]}px`, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = { ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none" };
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.sm,
  borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top",
};
const TH: React.CSSProperties = { ...TD, textAlign: "left", color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium, whiteSpace: "nowrap" };

export const NOT_STATED = "Customer date not stated";

/** The two customer-date flags, each the server's. */
export function CustomerFlagBadges({ promiseAfter, atRisk, reasons, testId }: {
  promiseAfter?: boolean; atRisk?: boolean; reasons?: string[]; testId?: string;
}) {
  if (!promiseAfter && !atRisk) return null;
  const title = (reasons ?? []).join("\n") || undefined;
  return (
    <span data-testid={testId} title={title} style={{ display: "inline-flex", gap: spacing[4], flexWrap: "wrap" }}>
      {promiseAfter && <Pill tone="red">OEM promise after customer date</Pill>}
      {atRisk && <Pill tone="amber">Customer date at risk</Pill>}
    </span>
  );
}

/** What the Mark Won dialog sends: one date for every line, unless a line has
 *  its own. Lines left empty are not sent -- absent = not stated. */
export function datesFromForm(lines: { line_id: number }[], all: string, own: Record<number, string>): CustomerDateEntry[] {
  const out: CustomerDateEntry[] = [];
  for (const l of lines) {
    const d = (own[l.line_id] ?? "").trim() || all.trim();
    if (d) out.push({ line_id: l.line_id, date: d });
  }
  return out;
}

/** The Mark Won dialog's customer-date section and its confirm. */
export function WonDatesForm({ quoteId, busy, onConfirm, onCancel }: {
  quoteId: number; busy: boolean;
  onConfirm: (dates: CustomerDateEntry[]) => void;
  onCancel: () => void;
}) {
  const [lines, setLines] = useState<OrderStatusLine[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [all, setAll] = useState("");
  const [perLine, setPerLine] = useState(false);
  const [own, setOwn] = useState<Record<number, string>>({});
  useEffect(() => {
    void (async () => {
      const res = await getOrderStatus(quoteId);
      if (!res.ok) { setLoadError(res.error || "The quote's lines could not be read."); setLines([]); return; }
      setLines(res.view!.lines.filter((l) => !l.not_quoted));
    })();
  }, [quoteId]);
  const quoted = lines ?? [];
  return (
    <div data-testid="outcome-won-form" style={{ width: "100%", display: "grid", gap: spacing[8] }}>
      <div style={{ display: "flex", gap: spacing[8], alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ ...NOTE, display: "flex", gap: spacing[8], alignItems: "center" }}>
          Date promised to the customer (all lines)
          <input type="date" data-testid="outcome-won-date-all" aria-label="Customer date for all lines"
            style={INPUT} value={all} onChange={(e) => setAll(e.target.value)} />
        </label>
        {quoted.length > 1 && (
          <button type="button" data-testid="outcome-won-per-line" style={BTN} onClick={() => setPerLine((v) => !v)}>
            {perLine ? "Hide per-line dates" : "Set per line"}
          </button>
        )}
        <span style={NOTE}>Optional. Leave it empty if the customer stated no date.</span>
      </div>
      {loadError && <div role="alert" style={{ ...NOTE, color: colors.amber800 }}>{loadError} The quote can still be marked won without dates.</div>}
      {perLine && quoted.map((l) => (
        <label key={l.line_id} style={{ ...NOTE, display: "flex", gap: spacing[8], alignItems: "center" }}>
          <span style={{ minWidth: 220 }}>{l.line_no}. {l.part_number}</span>
          <input type="date" data-testid={`outcome-won-date-${l.line_id}`} aria-label={`Customer date for line ${l.line_no}`}
            style={INPUT} value={own[l.line_id] ?? ""} onChange={(e) => setOwn((v) => ({ ...v, [l.line_id]: e.target.value }))} />
          <span>{(own[l.line_id] ?? "") ? "" : all ? `uses ${formatDate(all)}` : "not stated"}</span>
        </label>
      ))}
      <div style={{ display: "flex", gap: spacing[8] }}>
        <button type="button" data-testid="outcome-won-confirm" disabled={busy || lines === null}
          style={busy || lines === null ? { ...PRIMARY, backgroundColor: colors.gray300, cursor: "not-allowed" } : PRIMARY}
          onClick={() => onConfirm(datesFromForm(quoted, all, own))}>Mark won</button>
        <button type="button" data-testid="outcome-won-cancel" style={BTN} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** A Won quote's customer dates, line by line; editable by its owner or an Admin. */
export function CustomerDatesPanel({ quoteId, canEdit, reloadKey = 0 }: {
  quoteId: number; canEdit: boolean; reloadKey?: number;
}) {
  const [lines, setLines] = useState<OrderStatusLine[] | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const res = await getOrderStatus(quoteId);
    // Fail loud: a failed read clears the rows.
    if (!res.ok) { setLines(null); setError(res.error || "The customer dates could not be read."); return; }
    setError(""); setLines(res.view!.lines);
  }, [quoteId]);
  useEffect(() => { void load(); }, [load, reloadKey]);

  const startEdit = () => {
    const v: Record<number, string> = {};
    for (const l of lines ?? []) v[l.line_id] = l.customer_promised_date ?? "";
    setVals(v); setEditing(true); setError("");
  };
  const save = async () => {
    const changed: CustomerDateEntry[] = (lines ?? [])
      .filter((l) => !l.not_quoted && (vals[l.line_id] ?? "") !== (l.customer_promised_date ?? ""))
      .map((l) => ({ line_id: l.line_id, date: (vals[l.line_id] ?? "") || null }));
    if (changed.length === 0) { setEditing(false); return; }
    setBusy(true);
    const res = await setCustomerDates(quoteId, changed);
    setBusy(false);
    if (!res.ok) { setError(res.error || "The customer dates were not saved."); return; }
    setLines(res.view!.lines); setEditing(false);
  };

  if (error && !lines) {
    return (
      <div role="alert" data-testid="cdates-error" style={{ ...NOTE, color: semanticColors.destructive, padding: spacing[12] }}>
        {error} <button type="button" style={BTN} onClick={() => { void load(); }}>Retry</button>
      </div>
    );
  }
  if (!lines) return null;
  return (
    <div data-testid="customer-dates" style={{ borderBottom: `1px solid ${semanticColors.border}` }}>
      <div style={{ display: "flex", gap: spacing[8], alignItems: "center", padding: `${spacing[8]}px ${spacing[12]}px`, flexWrap: "wrap" }}>
        <span style={{ fontWeight: typography.fontWeight.medium, fontSize: typography.fontSize.sm }}>Dates promised to the customer</span>
        <span style={NOTE}>Compared with each OEM's current promise. Every change is recorded.</span>
        {canEdit && !editing && (
          <button type="button" data-testid="cdates-edit" style={{ ...BTN, marginLeft: "auto" }} onClick={startEdit}>Edit dates</button>
        )}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["#", "Part", "Customer date", ""].map((h, i) => <th key={h || i} style={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.line_id} data-testid={`cdates-line-${l.line_no}`}>
              <td style={TD}>{l.line_no}</td>
              <td style={TD}>{l.part_number}</td>
              <td style={TD} data-testid={`cdates-date-${l.line_no}`}>
                {editing && !l.not_quoted ? (
                  <input type="date" data-testid={`cdates-input-${l.line_no}`} aria-label={`Customer date for line ${l.line_no}`}
                    style={INPUT} value={vals[l.line_id] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [l.line_id]: e.target.value }))} />
                ) : l.not_quoted ? <span style={{ color: semanticColors.textMuted }}>Not quoted</span>
                  : l.customer_promised_date ? formatDate(l.customer_promised_date)
                    : <span style={{ color: semanticColors.textMuted }}>{NOT_STATED}</span>}
              </td>
              <td style={TD}>
                <CustomerFlagBadges promiseAfter={l.promise_after_customer} atRisk={l.customer_date_at_risk}
                  testId={`cdates-flags-${l.line_no}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div role="alert" data-testid="cdates-save-error" style={{ ...NOTE, color: semanticColors.destructive, padding: spacing[8] }}>{error}</div>}
      {editing && (
        <div style={{ display: "flex", gap: spacing[8], padding: spacing[8] }}>
          <button type="button" data-testid="cdates-save" disabled={busy}
            style={busy ? { ...PRIMARY, backgroundColor: colors.gray300, cursor: "not-allowed" } : PRIMARY}
            onClick={() => { void save(); }}>{busy ? "Saving…" : "Save dates"}</button>
          <button type="button" data-testid="cdates-cancel" style={BTN} onClick={() => { setEditing(false); setError(""); }}>Cancel</button>
        </div>
      )}
    </div>
  );
}
